import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  FileText,
  Plus,
  Trash2,
  Printer,
  Download,
  Building2,
  Edit2,
  Eye,
  Search,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  AlertCircle,
  BarChart3
} from "lucide-react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Quotation() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const quotationRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState("list");
  const [companySettings, setCompanySettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [editingQuotation, setEditingQuotation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Quotation form state
  const [quotationData, setQuotationData] = useState({
    company: "",
    contact_person: "",
    contact_info: "",
    date: new Date().toISOString().split('T')[0],
    items: [
      { qty: "", description: "", price: "", amount: "" }
    ],
    payment_terms: "50% down payment upon approval; and remaining balance upon delivery of the complete goods. If for shipping; the final balance will be paid before shipping the goods and the shipping fee will be collected to the customer.",
    production_time: "8-10 working days (excluding Sundays and holidays) upon 50% down payment, and approval of final sample. Day 1 will start the next day from the day of down payment.",
    notes: "- Sampling fee, according to price above.\n- Sampling is 2-5 working days (excluding Sundays and holidays).\n- The sample product is for pick-up or for shipping only. Shipping fee will be collected to the customer.\n- Price is subject to change after only one batch of additional orders and within 30 days only. Succeeding additional orders will be quoted again depending on its quantity and terms.",
    status: "draft"
  });

  // Bank details
  const bankDetails = [
    { bank: "BPI", accountName: "Kathleen Mae C. Garcia", accountNo: "0279107543", color: "#D4190C" },
    { bank: "BDO", accountName: "Kathleen Mae C. Garcia", accountNo: "002560154796", color: "#003D79" },
    { bank: "GCash", accountName: "Kathleen Mae C. Garcia", accountNo: "0917 917 2327", color: "#007DFE" },
    { bank: "BDO (Corporate)", accountName: "KMANNA PRINTS CORPORATION", accountNo: "012218002340", color: "#003D79" }
  ];

  const authHeaders = { headers: { Authorization: `Bearer ${accessToken}` } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, quotationsRes, statsRes] = await Promise.all([
        axios.get(`${API}/settings`, authHeaders),
        axios.get(`${API}/quotations`, authHeaders),
        axios.get(`${API}/quotations/stats/summary`, authHeaders)
      ]);
      setCompanySettings(settingsRes.data);
      setQuotations(quotationsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Still try to get settings even if quotations fail (new endpoint)
      try {
        const settingsRes = await axios.get(`${API}/settings`, authHeaders);
        setCompanySettings(settingsRes.data);
      } catch (e) {
        console.error("Error fetching settings:", e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...quotationData.items];
    newItems[index][field] = value;
    
    if (field === 'qty' || field === 'price') {
      const qty = parseFloat(newItems[index].qty) || 0;
      const price = parseFloat(newItems[index].price) || 0;
      newItems[index].amount = (qty * price).toFixed(2);
    }
    
    setQuotationData({ ...quotationData, items: newItems });
  };

  const addItem = () => {
    setQuotationData({
      ...quotationData,
      items: [...quotationData.items, { qty: "", description: "", price: "", amount: "" }]
    });
  };

  const removeItem = (index) => {
    if (quotationData.items.length > 1) {
      const newItems = quotationData.items.filter((_, i) => i !== index);
      setQuotationData({ ...quotationData, items: newItems });
    }
  };

  const calculateTotal = () => {
    return quotationData.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const resetForm = () => {
    setQuotationData({
      company: "",
      contact_person: "",
      contact_info: "",
      date: new Date().toISOString().split('T')[0],
      items: [{ qty: "", description: "", price: "", amount: "" }],
      payment_terms: "50% down payment upon approval; and remaining balance upon delivery of the complete goods. If for shipping; the final balance will be paid before shipping the goods and the shipping fee will be collected to the customer.",
      production_time: "8-10 working days (excluding Sundays and holidays) upon 50% down payment, and approval of final sample. Day 1 will start the next day from the day of down payment.",
      notes: "- Sampling fee, according to price above.\n- Sampling is 2-5 working days (excluding Sundays and holidays).\n- The sample product is for pick-up or for shipping only. Shipping fee will be collected to the customer.\n- Price is subject to change after only one batch of additional orders and within 30 days only. Succeeding additional orders will be quoted again depending on its quantity and terms.",
      status: "draft"
    });
    setEditingQuotation(null);
  };

  const handleSaveQuotation = async (status = "draft") => {
    if (!quotationData.company.trim()) {
      toast.error("Please enter company name");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...quotationData,
        status,
        total_amount: calculateTotal()
      };

      if (editingQuotation) {
        await axios.put(`${API}/quotations/${editingQuotation.id}`, payload, authHeaders);
        toast.success("Quotation updated successfully!");
      } else {
        await axios.post(`${API}/quotations`, payload, authHeaders);
        toast.success(`Quotation ${status === "sent" ? "sent" : "saved"} successfully!`);
      }
      
      fetchData();
      resetForm();
      setActiveTab("list");
    } catch (error) {
      console.error("Error saving quotation:", error);
      toast.error("Failed to save quotation");
    } finally {
      setSaving(false);
    }
  };

  const handleEditQuotation = (quotation) => {
    setEditingQuotation(quotation);
    setQuotationData({
      company: quotation.company || "",
      contact_person: quotation.contact_person || "",
      contact_info: quotation.contact_info || "",
      date: quotation.date || new Date().toISOString().split('T')[0],
      items: quotation.items?.length > 0 ? quotation.items : [{ qty: "", description: "", price: "", amount: "" }],
      payment_terms: quotation.payment_terms || "",
      production_time: quotation.production_time || "",
      notes: quotation.notes || "",
      status: quotation.status || "draft"
    });
    setActiveTab("create");
  };

  const handleUpdateStatus = async (quotationId, newStatus) => {
    try {
      await axios.put(`${API}/quotations/${quotationId}`, { status: newStatus }, authHeaders);
      toast.success(`Quotation marked as ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleDeleteQuotation = async (quotationId) => {
    if (!window.confirm("Are you sure you want to delete this quotation?")) return;
    
    try {
      await axios.delete(`${API}/quotations/${quotationId}`, authHeaders);
      toast.success("Quotation deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete quotation");
    }
  };

  const handlePreview = (quotation) => {
    setSelectedQuotation(quotation);
    setShowPreviewModal(true);
  };

  const handlePrint = (quotation = null) => {
    const data = quotation || quotationData;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatePrintHTML(data));
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  const handleDownloadPDF = async (quotation = null) => {
    const data = quotation || quotationData;
    toast.loading("Generating PDF...");
    
    try {
      // Create a temporary div for rendering
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = generatePrintHTML(data, true);
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      document.body.appendChild(tempDiv);
      
      const element = tempDiv.querySelector('.quotation-content');
      const canvas = await html2canvas(element, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`quotation-${data.company || 'customer'}-${data.date}.pdf`);
      
      document.body.removeChild(tempDiv);
      toast.dismiss();
      toast.success("PDF downloaded!");
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to generate PDF");
    }
  };

  const generatePrintHTML = (data, forCanvas = false) => {
    const total = data.items?.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0) || data.total_amount || 0;
    const logoUrl = companySettings?.logo || "";
    
    return `
      <html>
        <head>
          <title>Quotation - ${data.company || 'Customer'}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 0; padding: 15px; font-size: 11px; }
            .quotation-content { max-width: 800px; margin: 0 auto; background: white; padding: 15px; }
            .header { text-align: center; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #C00; }
            .header img { max-height: 60px; margin-bottom: 5px; }
            .header h1 { margin: 0; font-size: 22px; color: #C00; font-weight: bold; }
            .header p { margin: 3px 0; color: #333; font-size: 11px; }
            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 15px; padding: 10px; background: #f9f9f9; border-radius: 5px; }
            .info-grid .left { text-align: left; }
            .info-grid .right { text-align: right; }
            .info-grid p { margin: 4px 0; }
            .info-grid strong { color: #333; }
            .salutation { margin: 10px 0 15px 0; font-size: 11px; }
            table { width: 100%; border-collapse: collapse; margin: 10px 0; }
            th { background-color: #C00; color: white; padding: 8px 10px; text-align: left; font-weight: bold; font-size: 11px; }
            th.text-center { text-align: center; }
            th.text-right { text-align: right; }
            td { border: 1px solid #ddd; padding: 8px 10px; vertical-align: top; }
            td.text-center { text-align: center; }
            td.text-right { text-align: right; }
            .item-description { font-size: 10px; line-height: 1.4; }
            .total-row { background-color: #f5f5f5; }
            .total-row td { font-weight: bold; font-size: 12px; }
            .section { margin: 12px 0; }
            .section-title { font-weight: bold; color: #C00; margin-bottom: 3px; font-size: 11px; }
            .section-content { font-size: 10px; line-height: 1.5; color: #333; }
            .payment-section { border-top: 2px solid #C00; padding-top: 12px; margin-top: 15px; }
            .payment-title { font-weight: bold; text-align: center; margin-bottom: 10px; color: #C00; font-size: 12px; }
            .bank-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
            .bank-item { padding: 8px; border: 1px solid #ddd; border-radius: 4px; font-size: 10px; border-left-width: 4px; }
            .bank-name { font-weight: bold; margin-bottom: 2px; }
            .bank-account { font-family: monospace; font-size: 11px; }
            .footer { text-align: center; margin-top: 15px; padding-top: 10px; border-top: 1px solid #ddd; }
            .footer .highlight { color: #C00; font-weight: bold; font-size: 11px; }
            .footer .thanks { margin-top: 5px; font-size: 11px; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="quotation-content">
            <!-- Header with Logo -->
            <div class="header">
              ${logoUrl ? `<img src="${logoUrl}" alt="Logo" />` : ''}
              <h1>MANNA PRINTS</h1>
              <p>0975 762 9267</p>
            </div>
            
            <!-- Client Info Grid -->
            <div class="info-grid">
              <div class="left">
                <p><strong>Company:</strong> ${data.company || "_______________"}</p>
                <p><strong>Contact Person:</strong> ${data.contact_person || "_______________"}</p>
              </div>
              <div class="right">
                <p><strong>Date:</strong> ${formatDate(data.date)}</p>
                <p><strong>Contact Info:</strong> ${data.contact_info || "_______________"}</p>
              </div>
            </div>
            
            <!-- Salutation -->
            <p class="salutation">Dear Sir/Ma'am,<br/>Thank you for your inquiry. As to our known delivery and payment conditions, we would like to quote the following:</p>
            
            <!-- Items Table -->
            <table>
              <thead>
                <tr>
                  <th class="text-center" style="width: 60px;">QTY</th>
                  <th>DESCRIPTION</th>
                  <th class="text-right" style="width: 100px;">PRICE</th>
                  <th class="text-right" style="width: 100px;">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                ${(data.items || []).map(item => `
                  <tr>
                    <td class="text-center">${item.qty || "-"}</td>
                    <td class="item-description">${item.description || "-"}</td>
                    <td class="text-right">${item.price ? formatCurrency(item.price) : "-"}</td>
                    <td class="text-right">${item.amount ? formatCurrency(item.amount) : "-"}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <!-- Terms Sections -->
            <div class="section">
              <p class="section-title">Mode of Payment:</p>
              <p class="section-content">${data.payment_terms || ""}</p>
            </div>
            <div class="section">
              <p class="section-title">Production:</p>
              <p class="section-content">${data.production_time || ""}</p>
            </div>
            <div class="section">
              <p class="section-title">Others:</p>
              <p class="section-content" style="white-space: pre-line;">${data.notes || ""}</p>
            </div>
            
            <!-- Payment Details - 2 Columns -->
            <div class="payment-section">
              <p class="payment-title">PAYMENT DETAILS</p>
              <div class="bank-grid">
                ${bankDetails.map(bank => `
                  <div class="bank-item" style="border-left-color: ${bank.color};">
                    <p class="bank-name" style="color: ${bank.color};">${bank.bank}</p>
                    <p>${bank.accountName}</p>
                    <p class="bank-account">${bank.accountNo}</p>
                  </div>
                `).join('')}
              </div>
            </div>
            
            <!-- Footer -->
            <div class="footer">
              <p class="highlight">Kindly send us your proof of payment.</p>
              <p class="thanks">Thank you very much and God bless you!</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: "bg-gray-500", icon: Edit2, label: "Draft" },
      sent: { color: "bg-blue-500", icon: Send, label: "Sent" },
      approved: { color: "bg-green-500", icon: CheckCircle, label: "Approved" },
      rejected: { color: "bg-red-500", icon: XCircle, label: "Rejected" },
      expired: { color: "bg-orange-500", icon: AlertCircle, label: "Expired" }
    };
    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const filteredQuotations = quotations.filter(q => {
    const matchesSearch = !searchTerm || 
      q.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.quotation_number?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Quotations</h1>
              <p className="text-muted-foreground">Create and manage quotations</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <FileText className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Sent</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
                  </div>
                  <Send className="h-8 w-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-50 to-green-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Approved</p>
                    <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-50 to-red-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Rejected</p>
                    <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">This Month</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.this_month}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-yellow-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Approved Value</p>
                    <p className="text-lg font-bold text-purple-600">{formatCurrency(stats.total_approved_value)}</p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="list">
              <FileText className="h-4 w-4 mr-2" />
              Quotation List
            </TabsTrigger>
            <TabsTrigger value="create">
              <Plus className="h-4 w-4 mr-2" />
              {editingQuotation ? "Edit Quotation" : "New Quotation"}
            </TabsTrigger>
          </TabsList>

          {/* List Tab */}
          <TabsContent value="list">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row gap-4 justify-between">
                  <CardTitle>All Quotations</CardTitle>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search quotations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-36">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="approved">Approved</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={() => { resetForm(); setActiveTab("create"); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      New Quotation
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {filteredQuotations.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No quotations found</p>
                    <p className="text-sm">Create your first quotation to get started</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-secondary">
                        <tr>
                          <th className="text-left p-3">Quotation #</th>
                          <th className="text-left p-3">Company</th>
                          <th className="text-left p-3">Contact</th>
                          <th className="text-left p-3">Date</th>
                          <th className="text-right p-3">Amount</th>
                          <th className="text-center p-3">Status</th>
                          <th className="text-center p-3">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredQuotations.map(quotation => (
                          <tr key={quotation.id} className="border-t border-border hover:bg-secondary/50">
                            <td className="p-3">
                              <span className="font-mono text-xs">{quotation.quotation_number}</span>
                            </td>
                            <td className="p-3">
                              <p className="font-medium">{quotation.company}</p>
                            </td>
                            <td className="p-3">{quotation.contact_person || "-"}</td>
                            <td className="p-3">{formatDate(quotation.date)}</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(quotation.total_amount)}</td>
                            <td className="p-3 text-center">{getStatusBadge(quotation.status)}</td>
                            <td className="p-3">
                              <div className="flex justify-center gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handlePreview(quotation)} title="Preview">
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleEditQuotation(quotation)} title="Edit">
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handlePrint(quotation)} title="Print">
                                  <Printer className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(quotation)} title="Download PDF">
                                  <Download className="h-4 w-4" />
                                </Button>
                                {quotation.status === "draft" && (
                                  <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(quotation.id, "sent")} title="Mark as Sent" className="text-blue-600">
                                    <Send className="h-4 w-4" />
                                  </Button>
                                )}
                                {quotation.status === "sent" && (
                                  <>
                                    <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(quotation.id, "approved")} title="Approve" className="text-green-600">
                                      <CheckCircle className="h-4 w-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(quotation.id, "rejected")} title="Reject" className="text-red-600">
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteQuotation(quotation.id)} title="Delete" className="text-red-600">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Create/Edit Tab */}
          <TabsContent value="create">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Form Section */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" />
                      Client Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label>Company Name *</Label>
                        <Input
                          value={quotationData.company}
                          onChange={(e) => setQuotationData({ ...quotationData, company: e.target.value })}
                          placeholder="Enter company name"
                        />
                      </div>
                      <div>
                        <Label>Contact Person</Label>
                        <Input
                          value={quotationData.contact_person}
                          onChange={(e) => setQuotationData({ ...quotationData, contact_person: e.target.value })}
                          placeholder="Contact person name"
                        />
                      </div>
                      <div>
                        <Label>Contact Info</Label>
                        <Input
                          value={quotationData.contact_info}
                          onChange={(e) => setQuotationData({ ...quotationData, contact_info: e.target.value })}
                          placeholder="Phone / Email"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={quotationData.date}
                          onChange={(e) => setQuotationData({ ...quotationData, date: e.target.value })}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <FileText className="h-5 w-5" />
                        Quotation Items
                      </span>
                      <Button size="sm" onClick={addItem}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add Item
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {quotationData.items.map((item, index) => (
                      <div key={index} className="p-4 border rounded-lg space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Item {index + 1}</span>
                          {quotationData.items.length > 1 && (
                            <Button variant="ghost" size="sm" onClick={() => removeItem(index)} className="text-red-500">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <Label>Quantity</Label>
                            <Input type="number" value={item.qty} onChange={(e) => handleItemChange(index, 'qty', e.target.value)} placeholder="Qty" />
                          </div>
                          <div>
                            <Label>Unit Price (₱)</Label>
                            <Input type="number" value={item.price} onChange={(e) => handleItemChange(index, 'price', e.target.value)} placeholder="Price" />
                          </div>
                          <div>
                            <Label>Amount</Label>
                            <Input value={item.amount ? `₱${parseFloat(item.amount).toLocaleString()}` : ''} readOnly className="bg-secondary" />
                          </div>
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)} placeholder="Enter item description..." rows={2} />
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Terms & Conditions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Payment Terms</Label>
                      <Textarea value={quotationData.payment_terms} onChange={(e) => setQuotationData({ ...quotationData, payment_terms: e.target.value })} rows={3} />
                    </div>
                    <div>
                      <Label>Production Time</Label>
                      <Textarea value={quotationData.production_time} onChange={(e) => setQuotationData({ ...quotationData, production_time: e.target.value })} rows={2} />
                    </div>
                    <div>
                      <Label>Additional Notes</Label>
                      <Textarea value={quotationData.notes} onChange={(e) => setQuotationData({ ...quotationData, notes: e.target.value })} rows={4} />
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button variant="outline" onClick={() => handleSaveQuotation("draft")} disabled={saving}>
                    <Clock className="h-4 w-4 mr-2" />
                    Save as Draft
                  </Button>
                  <Button onClick={() => handleSaveQuotation("sent")} disabled={saving}>
                    <Send className="h-4 w-4 mr-2" />
                    {editingQuotation ? "Update & Send" : "Save & Send"}
                  </Button>
                </div>
              </div>

              {/* Preview Section */}
              <div>
                <Card className="sticky top-4">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Preview</CardTitle>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handlePrint()}>
                          <Printer className="h-4 w-4 mr-1" />
                          Print
                        </Button>
                        <Button size="sm" onClick={() => handleDownloadPDF()}>
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div ref={quotationRef} className="bg-white p-4 border rounded-lg overflow-auto max-h-[70vh]">
                      {/* Header */}
                      <div className="text-center border-b-2 border-red-600 pb-3 mb-4">
                        {companySettings?.logo && <img src={companySettings.logo} alt="Logo" className="max-h-14 mx-auto mb-2" />}
                        <h1 className="text-xl font-bold text-red-700">MANNA PRINTS</h1>
                        <p className="text-sm text-gray-600">0975 762 9267</p>
                      </div>
                      
                      {/* Client Info Grid */}
                      <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded text-xs">
                        <div>
                          <p><strong>Company:</strong> {quotationData.company || "_______________"}</p>
                          <p><strong>Contact Person:</strong> {quotationData.contact_person || "_______________"}</p>
                        </div>
                        <div className="text-right">
                          <p><strong>Date:</strong> {formatDate(quotationData.date)}</p>
                          <p><strong>Contact Info:</strong> {quotationData.contact_info || "_______________"}</p>
                        </div>
                      </div>
                      
                      {/* Salutation */}
                      <p className="text-xs mb-3">Dear Sir/Ma&apos;am,<br/>Thank you for your inquiry. As to our known delivery and payment conditions, we would like to quote the following:</p>
                      
                      {/* Items Table */}
                      <table className="w-full border-collapse mb-4 text-xs">
                        <thead>
                          <tr className="bg-red-700 text-white">
                            <th className="p-2 text-center w-14">QTY</th>
                            <th className="p-2 text-left">DESCRIPTION</th>
                            <th className="p-2 text-right w-20">PRICE</th>
                            <th className="p-2 text-right w-24">AMOUNT</th>
                          </tr>
                        </thead>
                        <tbody>
                          {quotationData.items.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-200">
                              <td className="p-2 text-center border-l border-r border-gray-200">{item.qty || "-"}</td>
                              <td className="p-2 text-xs leading-tight">{item.description || "-"}</td>
                              <td className="p-2 text-right border-l border-r border-gray-200">{item.price ? formatCurrency(item.price) : "-"}</td>
                              <td className="p-2 text-right border-r border-gray-200">{item.amount ? formatCurrency(item.amount) : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      
                      {/* Terms */}
                      <div className="text-xs space-y-3 mb-4">
                        <div>
                          <p className="font-bold text-red-700">Mode of Payment:</p>
                          <p className="text-gray-700 leading-tight">{quotationData.payment_terms}</p>
                        </div>
                        <div>
                          <p className="font-bold text-red-700">Production:</p>
                          <p className="text-gray-700 leading-tight">{quotationData.production_time}</p>
                        </div>
                        <div>
                          <p className="font-bold text-red-700">Others:</p>
                          <p className="text-gray-700 leading-tight whitespace-pre-line">{quotationData.notes}</p>
                        </div>
                      </div>
                      
                      {/* Payment Details - 2 Columns */}
                      <div className="border-t-2 border-red-600 pt-3">
                        <p className="font-bold text-center text-red-700 mb-3 text-sm">PAYMENT DETAILS</p>
                        <div className="grid grid-cols-2 gap-2">
                          {bankDetails.map((bank, idx) => (
                            <div key={idx} className="p-2 border rounded text-xs" style={{ borderLeftWidth: '4px', borderLeftColor: bank.color }}>
                              <p className="font-bold" style={{ color: bank.color }}>{bank.bank}</p>
                              <p>{bank.accountName}</p>
                              <p className="font-mono text-xs">{bank.accountNo}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Footer */}
                      <div className="text-center mt-4 pt-3 border-t">
                        <p className="text-red-700 font-bold text-xs">Kindly send us your proof of payment.</p>
                        <p className="text-xs mt-1">Thank you very much and God bless you!</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Quotation Preview - {selectedQuotation?.quotation_number}</DialogTitle>
          </DialogHeader>
          {selectedQuotation && (
            <div className="bg-white p-4 border rounded-lg">
              {/* Header */}
              <div className="text-center border-b-2 border-red-600 pb-3 mb-4">
                {companySettings?.logo && <img src={companySettings.logo} alt="Logo" className="max-h-14 mx-auto mb-2" />}
                <h1 className="text-xl font-bold text-red-700">MANNA PRINTS</h1>
                <p className="text-sm text-gray-600">0975 762 9267</p>
              </div>
              
              {/* Client Info Grid */}
              <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-gray-50 rounded text-sm">
                <div>
                  <p><strong>Company:</strong> {selectedQuotation.company}</p>
                  <p><strong>Contact Person:</strong> {selectedQuotation.contact_person || "-"}</p>
                </div>
                <div className="text-right">
                  <p><strong>Date:</strong> {formatDate(selectedQuotation.date)}</p>
                  <p><strong>Contact Info:</strong> {selectedQuotation.contact_info || "-"}</p>
                </div>
              </div>
              
              {/* Salutation */}
              <p className="text-sm mb-3">Dear Sir/Ma&apos;am,<br/>Thank you for your inquiry. As to our known delivery and payment conditions, we would like to quote the following:</p>
              
              {/* Items Table */}
              <table className="w-full border-collapse mb-4 text-sm">
                <thead>
                  <tr className="bg-red-700 text-white">
                    <th className="p-2 text-center w-16">QTY</th>
                    <th className="p-2 text-left">DESCRIPTION</th>
                    <th className="p-2 text-right w-24">PRICE</th>
                    <th className="p-2 text-right w-28">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedQuotation.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-200">
                      <td className="p-2 text-center border-l border-r border-gray-200">{item.qty || "-"}</td>
                      <td className="p-2 text-sm">{item.description || "-"}</td>
                      <td className="p-2 text-right border-l border-r border-gray-200">{item.price ? formatCurrency(item.price) : "-"}</td>
                      <td className="p-2 text-right border-r border-gray-200">{item.amount ? formatCurrency(item.amount) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Terms */}
              <div className="text-sm space-y-3 mb-4">
                <div>
                  <p className="font-bold text-red-700">Mode of Payment:</p>
                  <p className="text-gray-700">{selectedQuotation.payment_terms}</p>
                </div>
                <div>
                  <p className="font-bold text-red-700">Production:</p>
                  <p className="text-gray-700">{selectedQuotation.production_time}</p>
                </div>
                <div>
                  <p className="font-bold text-red-700">Others:</p>
                  <p className="text-gray-700 whitespace-pre-line">{selectedQuotation.notes}</p>
                </div>
              </div>
              
              {/* Payment Details - 2 Columns */}
              <div className="border-t-2 border-red-600 pt-3">
                <p className="font-bold text-center text-red-700 mb-3">PAYMENT DETAILS</p>
                <div className="grid grid-cols-2 gap-2">
                  {bankDetails.map((bank, idx) => (
                    <div key={idx} className="p-2 border rounded text-sm" style={{ borderLeftWidth: '4px', borderLeftColor: bank.color }}>
                      <p className="font-bold" style={{ color: bank.color }}>{bank.bank}</p>
                      <p>{bank.accountName}</p>
                      <p className="font-mono">{bank.accountNo}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Footer */}
              <div className="text-center mt-4 pt-3 border-t">
                <p className="text-red-700 font-bold">Kindly send us your proof of payment.</p>
                <p className="mt-1">Thank you very much and God bless you!</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => handlePrint(selectedQuotation)}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button onClick={() => handleDownloadPDF(selectedQuotation)}>
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
